import React, { useEffect } from "react";
import { Col, Row } from "reactstrap";
import * as Section from "@repoflow/components/section";
import { useEditor } from "@repoflow/components/editor";

import { useEditorPage } from "../context";
import { translatedNow } from "../translation";
import * as CodeType from "./types/code";
import * as SectionType from "./types/section";
import * as ParagraphType from "./types/paragraph";
import * as BlogPostType from "./types/blog-post";
import * as BlogListType from "./types/blog-list";
import * as BlogPostCollectionType from "./types/blog-post-collection";
import * as ConfigRoute from "./config";

// One element, on its own screen, at `/edit/element/:index`.
//
// The screen a list row cannot be: a `code` element carries three code fields,
// a type declaration and the values filled into it, and each of those is a tab.
//
// The trick, and it is gen-2's: this mounts a Section.Provider whose `state`
// READS one entry of the page's `elements` array and WRITES the whole array
// back. Everything below is then an ordinary form over an ordinary value object
// and knows nothing about being inside a list — while the page above it still
// has one value, one dirty flag and one Save.
export const Content = ({
  match: {
    params: { index: indexParam },
  },
}) => {
  // The route hands the index over as a string; everything that uses it — the
  // array read, the preview's selection, the crosshair's active check — is
  // numeric. The route pattern is `:index(\\d+)`, so this cannot be NaN.
  const index = Number(indexParam);

  const context = Section.useSectionContext();
  const { schema, state, base, basepath, attributes } = context;
  const { closeElement } = useEditorPage();
  const { setSelected } = useEditor();

  const { values, setValues } = state;
  const elements = values.elements || [];
  const element = elements[index];

  // Opening an element selects it in the preview, and leaving deselects. Not
  // `select`, which toggles: the crosshair in the list may have selected this
  // same element a moment ago, and a toggle here would immediately clear it.
  useEffect(() => {
    setSelected(index);
    return () => setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Reachable by removing the last element while its own screen is open.
  if (!element) {
    return (
      <Row>
        <Col>
          <div className="alert alert-warning">
            No element #{index} on this page.{" "}
            <BackLink onClick={closeElement}>Back to the elements</BackLink>.
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Section.Provider
      // Remounted when the index changes: every tab below holds its own state
      // keyed on the element it opened with, and moving to the next element
      // without a remount would carry the previous one's open tab and scroll.
      // The route alone does not do it — moving from element 1 to element 2 is
      // the same component with new params, which is an update.
      key={index}
      schema={schema}
      // The element's own URL, not the editor's. Nothing below reads these yet;
      // they are what a route nested under an element would be resolved against,
      // and leaving them pointing at /edit is the kind of thing that is correct
      // until the first person adds one.
      base={`${base}/element/${index}`}
      basepath={`${basepath}/element/:index`}
      attributes={attributes}
      state={{
        values: element,
        setValues: (mod) => {
          const modified = [...values.elements];
          // The one funnel every per-element edit passes through — the three
          // type screens and the Element tab all write the element through
          // here — which is what makes this the place the translation mark is
          // kept up to date rather than three places. See ../translation.
          modified[index] = translatedNow(element, { ...mod });
          setValues({ ...values, elements: modified });
        },
      }}
    >
      {/* A button rather than a <Link>, even though the element has a URL of
          its own again. It replaces rather than pushes — the same thing every
          tab in the strip does — so that leaving an element does not leave a
          history entry you have to press Back through twice. */}
      <Row>
        <Col>
          <BackLink onClick={closeElement}>
            <i className="fa fa-angle-left"></i> Elements
          </BackLink>
        </Col>
      </Row>

      <ElementTabs element={element} />
    </Section.Provider>
  );
};

const BackLink = ({ onClick, children }) => (
  <button className="btn btn-link px-0" onClick={onClick}>
    {children}
  </button>
);

const ElementTabs = ({ element }) => {
  if (element.type === "code") {
    return <CodeType.Content />;
  }

  if (element.type === "section") {
    return <SectionType.Content />;
  }

  if (element.type === "paragraph") {
    return <ParagraphType.Content />;
  }

  if (element.type === "blog-post") {
    return <BlogPostType.Content />;
  }

  if (element.type === "blog-list") {
    return <BlogListType.Content />;
  }

  if (element.type === "blog-post-collection") {
    return <BlogPostCollectionType.Content />;
  }

  // An element saved before its type was chosen. The Element tab is where the
  // type is set, so this is recoverable rather than stuck.
  return <ConfigRoute.Only />;
};
